import { Button, Spin, Tooltip } from 'antd';
import { useState } from 'react';
import { ASSET_PHOTO_CONSTANT, TAssetPhotoContext, TPhotoItem } from '../constant';
import {
  EnlargeDialogFooter,
  EnlargeDialogImage,
  EnlargeDialogImageWrapper,
  EnlargeDialogModalWrapper,
} from '../AssetPhotoInquiryStyled';

const { TITLE, LABEL, MESSAGE } = ASSET_PHOTO_CONSTANT;

interface PhotoEnlargeDialogProps {
  open: boolean;
  photos: TPhotoItem[];
  currentIndex: number;
  assetInfo: TAssetPhotoContext | null;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) {
    return '—';
  }
  const datePart = dateStr.split('T')[0]; // strip time from ISO 8601 if present
  const parts = datePart.split('-');
  if (parts.length !== 3) {
    return dateStr;
  }
  return `${parts[0]}/${parts[1]}/${parts[2]}`;
}

export function PhotoEnlargeDialog({
  open,
  photos,
  currentIndex,
  assetInfo,
  onClose,
  onPrev,
  onNext,
}: PhotoEnlargeDialogProps) {
  const [imgLoading, setImgLoading] = useState(true);
  const [imgError, setImgError] = useState(false);

  const currentPhoto = photos[currentIndex] ?? null;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === photos.length - 1;

  const handleImageLoad = () => {
    setImgLoading(false);
    setImgError(false);
  };

  const handleImageError = () => {
    setImgLoading(false);
    setImgError(true);
  };

  const handlePrev = () => {
    setImgLoading(true);
    setImgError(false);
    onPrev();
  };

  const handleNext = () => {
    setImgLoading(true);
    setImgError(false);
    onNext();
  };

  return (
    <EnlargeDialogModalWrapper
      open={open}
      title={TITLE.ENLARGE_DIALOG_TITLE}
      maskClosable={false}
      onCancel={onClose}
      footer={null}
      width="60vw"
      style={{ top: '5vh' }}
      centered={false}
    >
      {/* Asset info */}
      <div className="enlarge-dialog_asset-info">
        <div className="enlarge-dialog_info-row">
          <span className="enlarge-dialog_info-label">{LABEL.ASSET_NO}</span>
          <span className="enlarge-dialog_info-value">{assetInfo?.asset_no ?? '—'}</span>
        </div>
        <div className="enlarge-dialog_info-row">
          <span className="enlarge-dialog_info-label">{LABEL.ASSET_NAME}</span>
          <span className="enlarge-dialog_info-value">{assetInfo?.asset_name ?? '—'}</span>
        </div>
      </div>

      {/* Enlarged image */}
      <EnlargeDialogImageWrapper>
        {imgLoading && !imgError && (
          <div className="enlarge-dialog_spinner">
            <Spin size="large" />
          </div>
        )}
        {imgError ? (
          <Tooltip title={MESSAGE.E02}>
            <div className="enlarge-dialog_error-placeholder">
              <span className="enlarge-dialog_error-icon">🖼</span>
              <span className="enlarge-dialog_error-text">{MESSAGE.E02}</span>
            </div>
          </Tooltip>
        ) : (
          currentPhoto && (
            <EnlargeDialogImage
              src={currentPhoto.file_path}
              alt={`写真 ${currentIndex + 1}`}
              onLoad={handleImageLoad}
              onError={handleImageError}
              style={{ display: imgLoading ? 'none' : 'block' }}
            />
          )
        )}
      </EnlargeDialogImageWrapper>

      {/* 撮影日 */}
      <div className="enlarge-dialog_taken-at">
        <span className="enlarge-dialog_info-label">{LABEL.TAKEN_AT}</span>
        <span className="enlarge-dialog_info-value">
          {currentPhoto ? formatDate(currentPhoto.taken_at) : '—'}
        </span>
      </div>

      {/* Footer navigation */}
      <EnlargeDialogFooter>
        <div className="enlarge-dialog_nav-buttons">
          <Button onClick={handlePrev} disabled={isFirst}>
            {TITLE.BTN_PREV}
          </Button>
          <Button onClick={handleNext} disabled={isLast}>
            {TITLE.BTN_NEXT}
          </Button>
        </div>
        <Button type="primary" onClick={onClose}>
          {TITLE.BTN_CLOSE}
        </Button>
      </EnlargeDialogFooter>
    </EnlargeDialogModalWrapper>
  );
}
